import type { PageModules } from "@for-it/web-lib";
import type { ICookie } from "cookie-universal";
import {
    ErrorPage,
    ErrorProvider,
    LoadingPage,
    ModalProvider,
    PagesProvider,
    RoutingProvider,
    RpcClientImplementation,
    RpcClientProvider,
    parseRoutes,
} from "@for-it/web-lib";
import { DomainErrors } from "instance-management-domain";
import { httpUseCasesAdmin } from "instance-management-domain";
import { StrictMode, useMemo } from "react";
import { Toaster } from "react-hot-toast";
import { HydrationBoundary } from "./components/hydration-loading.tsx";
import { API_HOST } from "./environment.ts";
import "@for-it/web-lib/style.css";
import { errorMessages } from "./error-messages.ts";
import "./index.css";
import NotFoundPageAdmin from "./pages/not-found-page-admin.tsx";
import { Routes } from "./routes.tsx";

const pageModules = import.meta.glob("./pages/**/*.tsx") as PageModules;

const generatedRoutes = parseRoutes(Routes, pageModules);

export interface AppProps {
    url: string;
    cookies: ICookie;
}

export default function App({ url, cookies }: AppProps) {
    const rpcClient = useMemo(
        () =>
            new RpcClientImplementation(
                {
                    apiHost: API_HOST,
                    errorClasses: DomainErrors,
                    useCases: httpUseCasesAdmin,
                },
                cookies,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    return (
        <StrictMode>
            <HydrationBoundary>
                <RoutingProvider startingURL={url}>
                    <ErrorProvider errorMessages={errorMessages}>
                        <RpcClientProvider
                            rpcService={rpcClient}
                            cookies={cookies}
                        >
                            <ModalProvider>
                                <PagesProvider
                                    LoadingPage={LoadingPage}
                                    routes={generatedRoutes}
                                    ErrorPage={ErrorPage}
                                    NotFoundPage={NotFoundPageAdmin}
                                />
                            </ModalProvider>
                            <Toaster />
                        </RpcClientProvider>
                    </ErrorProvider>
                </RoutingProvider>
            </HydrationBoundary>
        </StrictMode>
    );
}
